import { useEffect, useState } from "react"
import {
  Box,
  Card,
  CardMedia,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
} from "@mui/material"
import { ReactComponent as DottedGridIcon } from "../../../assets/imgs/icons/dottedGrid.svg"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import useDeviceType from "../../../utils/useDeviceType"
import { getFullImageUrl } from "../../../utils/getFullImageUrl"

export default function VenueGallery({ tenant }) {
  const [openGallery, setOpenGallery] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedImages, setLoadedImages] = useState({})
  const [isLightboxImageLoaded, setIsLightboxImageLoaded] = useState(false)
  const { isMobile, isTablet } = useDeviceType()

  const galleryImages = tenant?.data?.gallery_images || []

  useEffect(() => {
    setIsLightboxImageLoaded(false)
  }, [currentIndex, openGallery])

  useEffect(() => {
    setLoadedImages({})
  }, [tenant?.data?.id, galleryImages.length])

  const handleOpenGallery = (index = 0) => {
    setCurrentIndex(index)
    setOpenGallery(true)
  }

  const handleCloseGallery = () => setOpenGallery(false)

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    )
  }

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    )
  }

  const handleImageLoaded = (index) => {
    setLoadedImages((prev) => ({
      ...prev,
      [index]: true,
    }))
  }

  return (
    <>
      {isMobile ? (
        // ====== MOBILE VIEW ======
        <Box sx={{ position: "relative", mb: 2 }}>
          <Card
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={() => handleOpenGallery(0)}
          >
            {!loadedImages[0] && (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: 220,
                }}
              />
            )}
            <CardMedia
              component="img"
              image={getFullImageUrl(galleryImages[0]?.image_url)}
              alt="Main"
              onLoad={() => handleImageLoaded(0)}
              onError={() => handleImageLoaded(0)}
              sx={{
                width: "100%",
                height: 220,
                objectFit: "cover",
                opacity: loadedImages[0] ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            />
          </Card>

          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              handleOpenGallery(0)
            }}
            sx={{
              position: "absolute",
              bottom: 15,
              right: 15,
              bgcolor: "background.secondary",
              borderRadius: "12px",
              width: 45,
              height: 45,
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
          >
            <DottedGridIcon width={20} height={20} />
          </IconButton>
        </Box>
      ) : (
        // ====== DESKTOP VIEW ======
        <Box
          sx={{
            position: "relative",
            display: "grid",
            gap: 2,
            mb: 2,
          }}
        >
          {/* ====== MAIN LARGE IMAGE ====== */}
          <Card
            sx={{
              gridColumn: "span 3",
              gridRow: "span 4",
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              width: isTablet ? "100%" : 808,
              height: isTablet ? 255 : 457,
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={() => handleOpenGallery(0)}
          >
            {!loadedImages[0] && (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            )}
            <CardMedia
              component="img"
              image={getFullImageUrl(galleryImages[0]?.image_url)}
              alt="Main"
              onLoad={() => handleImageLoaded(0)}
              onError={() => handleImageLoaded(0)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: loadedImages[0] ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            />
          </Card>

          {/* ====== TOP RIGHT SMALL IMAGE ====== */}
          <Card
            sx={{
              gridColumn: "4 / span 2",
              gridRow: "span 2",
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              width: isTablet ? "100%" : 384,
              height: isTablet ? 120 : 218,
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={() => handleOpenGallery(1)}
          >
            {!loadedImages[1] && (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            )}
            <CardMedia
              component="img"
              image={getFullImageUrl(galleryImages[1]?.image_url)}
              alt="Gallery 2"
              onLoad={() => handleImageLoaded(1)}
              onError={() => handleImageLoaded(1)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: loadedImages[1] ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            />
          </Card>

          {/* ====== BOTTOM RIGHT SMALL IMAGE ====== */}
          <Card
            sx={{
              gridColumn: "4 / span 2",
              gridRow: "3 / span 2",
              borderRadius: 2,
              overflow: "hidden",
              width: isTablet ? "100%" : 384,
              height: isTablet ? 120 : 218,
              position: "relative",
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={() => handleOpenGallery(2)}
          >
            <CardMedia
              component="img"
              image={getFullImageUrl(galleryImages[2]?.image_url)}
              alt="Gallery 3"
              onLoad={() => handleImageLoaded(2)}
              onError={() => handleImageLoaded(2)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: loadedImages[2] ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            />
            {!loadedImages[2] && (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            )}

            {/* ====== OVERLAY BUTTON ====== */}
            <Button
              variant="contained"
              startIcon={<DottedGridIcon />}
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                width: 182,
                height: 44,
                bgcolor: "background.secondary",
                color: "text.dark",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: 14,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#C5C9D1" },
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleOpenGallery(0)
              }}
            >
              Show all photos
            </Button>
          </Card>
        </Box>
      )}

      {/* ====== LIGHTBOX ====== */}
      <Dialog
        open={openGallery}
        onClose={handleCloseGallery}
        fullScreen
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "#0A0D12F2",
            },
          },
          paper: {
            sx: {
              backgroundColor: "transparent",
              color: "white",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            m: 0,
            position: "relative",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* ====== TOP BAR ====== */}
          <Box
            sx={{
              position: "absolute",
              top: { xs: 16, sm: 24, md: 30 },
              left: { xs: 16, sm: 24, md: 40 },
              right: { xs: 16, sm: 24, md: 40 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              zIndex: 3,
              mt: { xs: 2, md: 0 },
            }}
          >
            <Box sx={{ ml: { xs: 1.5, sm: 2.5, md: 16 } }}>
              <Box
                sx={{
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontWeight: 700,
                  color: "white",
                }}
              >
                {tenant?.data?.name}
              </Box>
              <Box
                sx={{
                  color: "white",
                  fontWeight: 400,
                  fontSize: { xs: 13, sm: 14, md: 16 },
                  opacity: 0.85,
                }}
              >
                {currentIndex + 1} / {galleryImages.length}
              </Box>
            </Box>

            <Button
              onClick={handleCloseGallery}
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontWeight: 500,
                py: 0.5,
                mr: { xs: 1.5, sm: 2.5, md: 16 },
              }}
            >
              Close
            </Button>
          </Box>

          {/* ====== MAIN IMAGE ====== */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              width: { xs: "95%", sm: "90%", md: "85%", lg: "70%" },
              maxWidth: 1000,
              height: { xs: "65%", sm: "70%", md: "75%" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: { xs: 1, sm: 3, md: 10 },
            }}
          >
            {!isLightboxImageLoaded && (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  zIndex: 1,
                }}
              />
            )}
            <img
              src={getFullImageUrl(galleryImages[currentIndex]?.image_url)}
              alt={`Slide ${currentIndex + 1}`}
              onLoad={() => setIsLightboxImageLoaded(true)}
              onError={() => setIsLightboxImageLoaded(true)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
                opacity: isLightboxImageLoaded ? 1 : 0,
                transition: "opacity 0.2s ease",
              }}
            />

            {/* ====== LEFT ARROW ====== */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: "absolute",
                left: { xs: 10, sm: 10, md: -60 },
                color: "white",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                zIndex: 3,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            {/* ====== RIGHT ARROW ====== */}
            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                right: { xs: 10, sm: 10, md: -60 },
                color: "white",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                zIndex: 3,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          {/* ====== THUMBNAIL STRIP ====== */}
          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                bottom: { sm: 20, md: 30 },
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: { sm: 1, md: 1.5 },
                overflowX: "auto",
                px: 2,
                py: 1,
                zIndex: 3,
              }}
            >
              {galleryImages.map((img, idx) => (
                <Box
                  key={img.id || idx}
                  component="img"
                  src={getFullImageUrl(img.image_url)}
                  alt={`Thumbnail ${idx + 1}`}
                  onClick={() => setCurrentIndex(idx)}
                  sx={{
                    width: { sm: 90, md: 120, lg: 140 },
                    height: { sm: 60, md: 75, lg: 85 },
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    border:
                      idx === currentIndex
                        ? "2px solid white"
                        : "2px solid transparent",
                    opacity: idx === currentIndex ? 1 : 0.6,
                    transition: "all 0.2s ease",
                    "&:hover": { opacity: 1 },
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
